import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';

import GoalInfo from './GoalInfo';
import AllIndicatorsForGoalList from './AllIndicatorsForGoalList';
import SingleIndicatorDetails from './SingleIndicatorDetails';
import AllGoalIndicatorsMobile from './AllGoalIndicatorsMobile';
import SDGSpinner from './SDGSpinner';

import { GoalsContext } from '../State/GoalsContext.ts';
import { IndicatorsContext } from '../State/IndicatorsContext.ts';
import { normalizeGoalType } from '../utils/goalTypeUtils';
import '../utils/sharedStyles.css';

const AllGoalIndicators = () => {
  const { goalType: urlGoalType, goalId, indicatorId } = useParams();
  const goalType = normalizeGoalType(urlGoalType);

  const { fetchGoal, selectedGoal, loading: goalLoading } = useContext(GoalsContext);
  const {
    fetchAllIndicatorsByGoal,
    fetchSingleIndicator,
    indicators,
    loading: indicatorsLoading
  } = useContext(IndicatorsContext);

  const [selectedIndicatorId, setSelectedIndicatorId] = useState(indicatorId || null);
  const [fullIndicatorData, setFullIndicatorData] = useState(null);
  const [leftColumnWidth, setLeftColumnWidth] = useState(25); // Default 25%
  const [isResizing, setIsResizing] = useState(false);
  const resizeHandleRef = useRef(null);
  const containerRef = useRef(null);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  // Sync selectedIndicatorId with URL parameter
  useEffect(() => {
    if (isMobile) return;
    if (indicatorId) {
      setSelectedIndicatorId(indicatorId);
    } else {
      setSelectedIndicatorId(null);
      setFullIndicatorData(null);
    }
  }, [indicatorId, isMobile]);

  // Always fetch full indicator preview when selected to ensure we have latest assessment data
  useEffect(() => {
    if (isMobile) return;
    if (selectedIndicatorId) {
      // Always fetch fresh preview data to ensure we have the latest assessment
      fetchSingleIndicator(parseInt(selectedIndicatorId), goalType).then(fullData => {
        if (fullData) {
          setFullIndicatorData(fullData);
        } else {
          // Fallback to indicator from list if preview fetch fails
          const selectedIndicator = indicators.find((i) => i.id === parseInt(selectedIndicatorId));
          if (selectedIndicator) {
            setFullIndicatorData(selectedIndicator);
          }
        }
      });
    } else {
      setFullIndicatorData(null);
    }
  }, [selectedIndicatorId, goalType, fetchSingleIndicator, isMobile]);

  useEffect(() => {
    if (isMobile) return;
    const parsedGoalId = parseInt(goalId);
    console.log('📊 AllGoalIndicators goal useEffect:', { 
      goalId: parsedGoalId, 
      goalType,
      selectedGoalId: selectedGoal?.id,
      shouldFetch: !selectedGoal || selectedGoal.id !== parsedGoalId
    });
    
    if (goalId && (!selectedGoal || selectedGoal.id !== parsedGoalId)) {
      console.log('📊 Fetching goal:', parsedGoalId);
      fetchGoal(parsedGoalId);
    }
  }, [goalId, goalType, fetchGoal, selectedGoal, isMobile]);

  // Use ref to track if we've already fetched for this goalId+goalType combo
  const fetchedRef = useRef({ goalId: null, goalType: null });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    if (isMobile) return;
    const parsedGoalId = parseInt(goalId);
    const shouldFetch = goalId && goalType && 
      (fetchedRef.current.goalId !== parsedGoalId || fetchedRef.current.goalType !== goalType);
    
    if (shouldFetch) {
      console.log('📊 AllGoalIndicators indicators useEffect:', { 
        goalId: parsedGoalId, 
        goalType,
        shouldFetch
      });
      fetchedRef.current = { goalId: parsedGoalId, goalType };
      setIsInitialLoad(true);
      // Use a small delay to ensure loading state is visible, then check if fetch completed
      fetchAllIndicatorsByGoal(parsedGoalId, goalType).then(() => {
        // Wait a bit for state to update, then clear initial load
        setTimeout(() => setIsInitialLoad(false), 100);
      }).catch(() => {
        setIsInitialLoad(false);
      });
    } else {
      // If we don't need to fetch, wait a moment to ensure indicators are loaded
      setTimeout(() => setIsInitialLoad(false), 100);
    }
  }, [goalId, goalType, fetchAllIndicatorsByGoal, isMobile]);

  const selectedIndicator = useMemo(() => {
    return indicators.find((i) => i.id === parseInt(selectedIndicatorId)) || null;
  }, [selectedIndicatorId, indicators]);

  // Resize handlers - optimized for smooth, immediate updates
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current || !resizeHandleRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;
      const newWidthPercent = (mouseX / containerWidth) * 100;

      // Clamp between 15% and 35%
      const clampedWidth = Math.max(15, Math.min(35, newWidthPercent));
      
      // Log current width percentage
      console.log(`Left column width: ${clampedWidth.toFixed(2)}%`);
      
      // Update handle position directly via DOM for instant response
      if (resizeHandleRef.current) {
        resizeHandleRef.current.style.left = `${clampedWidth}%`;
      }
      
      // Also update state for column width (this will cause a re-render but handle is already positioned)
      setLeftColumnWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleIndicatorSelect = (indicatorId) => {
    setSelectedIndicatorId(indicatorId);
  };

  // Show loading spinner while fetching goal or indicators, or if goal is not loaded yet
  // Also show spinner during initial load to prevent blank page
  // Use mobile component on mobile devices
  if (isMobile) {
    return <AllGoalIndicatorsMobile />;
  }
  
  if (goalLoading || indicatorsLoading || !selectedGoal || isInitialLoad) {
    return <SDGSpinner size="lg" text="Loading indicators..." />;
  }

  return (
    <div className="container-full">
      <Container 
        fluid 
        ref={containerRef}
        className={`goal-details-container ${isResizing ? 'resizing' : ''}`}
      >
        <Row className="row-full-height">
          <Col 
            xs={12} 
            md={4} 
            className="targets-column"
            style={{ 
              display: 'flex', 
              flexDirection: 'column',
              flex: `0 0 ${leftColumnWidth}%`,
              minWidth: '15%',
              maxWidth: '35%'
            }}
          >
            <div className="goal-info-content">
              <GoalInfo goal={selectedGoal} />
            </div>
            <div className="targets-list">
              <div className="section-title section-title-spacing">
                <h6 className="sdg-section-title">Indicators</h6>
              </div>
              <AllIndicatorsForGoalList
                indicators={indicators}
                goalId={goalId}
                activeIndicatorId={selectedIndicatorId || indicatorId}
                onIndicatorSelect={handleIndicatorSelect}
              />
            </div>
          </Col>
          
          {/* Resize Handle */}
          <div
            ref={resizeHandleRef}
            className={`resize-handle ${isResizing ? 'resizing' : ''}`}
            onMouseDown={handleMouseDown}
            title="Drag to resize columns"
            style={{
              position: 'absolute',
              left: `${leftColumnWidth}%`,
              transform: 'translateX(-50%)',
              zIndex: 1000
            }}
          />
          
          <Col 
            xs={12} 
            md={8} 
            className="indicators-column"
            style={{ 
              flex: '1',
              marginLeft: 0
            }}
          >
            {(selectedIndicator || fullIndicatorData) ? (
              <SingleIndicatorDetails indicator={fullIndicatorData || selectedIndicator} />
            ) : (
              <div>
                <p className='help-text'>
                  <i className="bi bi-bar-chart-line-fill icon-help"></i>
                  Please select an indicator on the left-hand side to view details
                </p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AllGoalIndicators;
